import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { gql } from '@apollo/client';
import { getApolloClient } from '../../../utils/apolloUtils';
import { getApolloClientMfe } from '../../../utils/apolloUtilsMfe';
import {
  selectFilterValue,
  selectFilterLoading,
  selectFilter,
} from '../../../store/slices/filterSliceV2';
import store from '../../../store/store';

/**
 * useGraphql Hook - Simplified GraphQL query hook using Redux filters v2
 *
 * Features:
 * - Waits for all Redux filters to be ready (not loading and have values) before executing query
 * - Supports mixing filter placeholders ({{filterKey}}) with direct values
 * - Automatically tracks filter dependencies and re-fetches when they change
 *
 * @param {Object} config - Configuration object
 * @param {string} config.query - GraphQL query string
 * @param {Object} [config.variables] - Variables object with {{redux_filter_key}} placeholders and/or direct values
 * @param {string} [config.fetchPolicy='cache-first'] - Apollo fetch policy
 * @param {boolean} [config.enabled=true] - Whether the query is enabled and should execute
 *
 * @returns {Object} { data, isLoading, error, refetch, setShouldFetch }
 *
 * @example
 * const { data, isLoading } = useGraphql({
 *   query: `query GetData($programId: Int!, $limit: Int!) {
 *     getData(programId: $programId, limit: $limit) { id name }
 *   }`,
 *   variables: {
 *     programId: '{{selectedProgram}}',  // From Redux filter (waits for it to be ready)
 *     limit: 10                           // Direct value (used as-is)
 *   }
 * });
 */
export function useGraphql(config = {}) {
  const {
    query: queryString,
    variables: variablesTemplate = {},
    fetchPolicy = 'cache-first',
    enabled = true,
  } = config;

  // Get Apollo client based on MFE mode
  const isMFE = store.getState().app.isMFE;
  const client = isMFE ? getApolloClientMfe() : getApolloClient();

  // Serialize variables to avoid object reference issues in dependencies
  const variablesString = JSON.stringify(variablesTemplate);

  // Store variables in ref to use latest value without triggering re-renders
  const variablesRef = useRef(variablesTemplate);
  variablesRef.current = variablesTemplate;

  // State management
  const [shouldFetch, setShouldFetch] = useState(enabled);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Sync shouldFetch with enabled parameter changes
  useEffect(() => {
    setShouldFetch(enabled);
  }, [enabled]);
  // Track current request to handle race conditions
  const requestIdRef = useRef(0);

  // Extract filter keys from variables template
  const filterKeys = useMemo(
    () => extractFilterKeys(variablesTemplate),
    [variablesString]
  );

  // Subscribe to all required Redux filters (values and loading states)
  // Following PsdFilter pattern for checking if filters are ready
  // Using separate selectors to avoid creating new object references
  const filterValues = useSelector((state) => {
    if (filterKeys.length === 0) {
      return null;
    }

    const values = {};
    filterKeys.forEach((key) => {
      const { filterId, path } = parseFilterKey(key);
      const value = selectFilterValue(state, filterId);
      values[key] = path ? getNestedValue(value, path) : value;
    });
    return values;
  }, shallowEqual);

  const filterLoadingStates = useSelector((state) => {
    if (filterKeys.length === 0) {
      return null;
    }

    return filterKeys.map((key) => {
      const { filterId } = parseFilterKey(key);
      return selectFilterLoading(state, filterId);
    });
  }, shallowEqual);

  // Check if all referenced filters exist in Redux (have been registered)
  const allFiltersExist = useSelector((state) => {
    if (filterKeys.length === 0) {
      return true;
    }

    return filterKeys.every((key) => {
      const { filterId } = parseFilterKey(key);
      return selectFilter(state, filterId) !== null;
    });
  }, shallowEqual);

  // Compute derived state from the selector results
  // This avoids creating new objects in the selector itself
  const filterData = useMemo(() => {
    if (filterKeys.length === 0) {
      return { values: {}, loadingStates: [], allReady: true };
    }

    // First check: all referenced filters must exist in Redux
    // This prevents firing queries before PsdFilter components have registered their filters
    if (!allFiltersExist) {
      return {
        values: filterValues || {},
        loadingStates: filterLoadingStates || [],
        allReady: false,
      };
    }

    const values = filterValues || {};
    const loadingStates = filterLoadingStates || [];

    // Check if any filter is loading
    const anyLoading = loadingStates.some((loading) => loading);
    if (anyLoading) {
      return { values, loadingStates, allReady: false };
    }

    // Check if all filters have been initialized (not undefined)
    // Note: null is a valid value (e.g., year filter is null for non-Yearly frequency)
    const allHaveValues = Object.values(values).every(
      (value) => value !== undefined
    );

    return { values, loadingStates, allReady: allHaveValues };
  }, [filterKeys, filterValues, filterLoadingStates, allFiltersExist]);

  // Create dependency string for useEffect
  const filterDependencyString = JSON.stringify({
    values: filterData.values,
    loadingStates: filterData.loadingStates,
  });

  // Refetch callback
  const refetch = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  // Execute GraphQL query with debounce to let filter state settle
  useEffect(() => {
    if (!shouldFetch) {
      setIsLoading(false);
      return;
    }

    if (!queryString) {
      setError([new Error('Query is required in config')]);
      setIsLoading(false);
      return;
    }

    // Check if all required filters are ready (not loading and have values)
    if (!filterData.allReady) {
      // Keep loading state true while waiting for filters
      setIsLoading(true);
      return;
    }

    setIsLoading(true);

    // Small debounce (50ms) to let React state settle before firing query
    // This prevents race conditions where parent filter changes but child filter
    // hasn't had a chance to set loading=true yet
    const debounceTimer = setTimeout(() => {
      setData(null);
      setError(null);

      // Increment request ID to track this specific request
      const currentRequestId = ++requestIdRef.current;

      // Parse GraphQL query
      let gqlQuery;
      try {
        gqlQuery = gql`
          ${queryString}
        `;
      } catch (err) {
        console.error('Failed to parse GraphQL query:', err);
        setError([err]);
        setIsLoading(false);
        return;
      }

      // Resolve variables by replacing {{redux_filter_key}} placeholders with actual values
      const resolvedVariables = resolveVariables(
        variablesRef.current || {},
        filterData.values
      );

      // Execute query
      client
        .query({
          query: gqlQuery,
          variables: resolvedVariables,
          fetchPolicy,
        })
        .then((result) => {
          // Ignore stale responses - only process if this is still the current request
          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          if (result.errors && result.errors.length > 0) {
            setError(result.errors);
          }

          setData(result.data || null);
          setIsLoading(false);
        })
        .catch((err) => {
          // Ignore errors from stale requests
          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          console.error('GraphQL query failed:', err);
          setError([err]);
          setIsLoading(false);
        });
    }, 50);

    return () => clearTimeout(debounceTimer);
  }, [
    shouldFetch,
    queryString,
    variablesString,
    client,
    fetchPolicy,
    filterDependencyString,
    refetchTrigger,
  ]);

  return {
    data,
    isLoading,
    error,
    refetch,
    setShouldFetch,
  };
}

/**
 * Extract filter keys from variables template
 * Only extracts keys that use {{filterKey}} notation
 * Direct values (numbers, booleans, non-placeholder strings) are ignored
 *
 * @param {Object} variablesTemplate - Variables object with potential {{filterKey}} placeholders
 * @returns {Array<string>} Array of filter keys to subscribe to
 *
 * @example
 * extractFilterKeys({
 *   programId: '{{selectedProgram}}',  // Extracted
 *   limit: 10,                          // Ignored (direct value)
 *   name: 'test'                        // Ignored (no placeholder)
 * })
 * // Returns: ['selectedProgram']
 */
function extractFilterKeys(variablesTemplate) {
  const keys = new Set();
  const regex = /\{\{([^}]+)\}\}/g;

  const traverse = (obj) => {
    if (typeof obj === 'string') {
      let match;
      while ((match = regex.exec(obj)) !== null) {
        keys.add(match[1]);
      }
    } else if (Array.isArray(obj)) {
      obj.forEach(traverse);
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach(traverse);
    }
  };

  traverse(variablesTemplate);
  return Array.from(keys);
}

/**
 * Resolve variables by replacing {{filterKey}} placeholders with actual filter values
 */
function resolveVariables(variablesTemplate, filterValues) {
  const resolved = {};

  Object.entries(variablesTemplate).forEach(([key, value]) => {
    resolved[key] = resolveValue(value, filterValues);
  });

  return resolved;
}

/**
 * Recursively resolve a value, replacing {{filterKey}} patterns
 * Direct values (numbers, booleans, non-placeholder strings) are passed through unchanged
 *
 * @param {*} value - Value to resolve (can be any type)
 * @param {Object} filterValues - Map of filter keys to their values
 * @returns {*} Resolved value
 *
 * @example
 * resolveValue('{{selectedProgram}}', { selectedProgram: 123 })
 * // Returns: 123
 *
 * @example
 * resolveValue(10, {})
 * // Returns: 10 (direct value passed through)
 *
 * @example
 * resolveValue({ id: '{{programId}}', limit: 20 }, { programId: 5 })
 * // Returns: { id: 5, limit: 20 }
 */
function resolveValue(value, filterValues) {
  if (typeof value === 'string') {
    // Check if entire string is a placeholder
    const fullMatch = value.match(/^\{\{([^}]+)\}\}$/);
    if (fullMatch) {
      const filterKey = fullMatch[1];
      return filterValues[filterKey] ?? null;
    }

    // Replace inline placeholders
    return value.replace(/\{\{([^}]+)\}\}/g, (match, filterKey) => {
      const filterValue = filterValues[filterKey];
      return filterValue !== null && filterValue !== undefined
        ? String(filterValue)
        : '';
    });
  }

  if (Array.isArray(value)) {
    return value.map((item) => resolveValue(item, filterValues));
  }

  if (value && typeof value === 'object') {
    const resolved = {};
    Object.entries(value).forEach(([k, v]) => {
      resolved[k] = resolveValue(v, filterValues);
    });
    return resolved;
  }

  // Direct values (numbers, booleans, null, etc.) are returned as-is
  return value;
}

/**
 * Parse filter key to support dot notation
 * Examples:
 *   "selectedProgram" -> { filterId: "selectedProgram", path: null }
 *   "selectedProgram.programId" -> { filterId: "selectedProgram", path: "programId" }
 *   "user.profile.name" -> { filterId: "user", path: "profile.name" }
 */
function parseFilterKey(key) {
  const dotIndex = key.indexOf('.');
  if (dotIndex === -1) {
    return { filterId: key, path: null };
  }
  return {
    filterId: key.substring(0, dotIndex),
    path: key.substring(dotIndex + 1),
  };
}

/**
 * Get nested value from object using dot notation path
 * Examples:
 *   getNestedValue({ programId: 123 }, "programId") -> 123
 *   getNestedValue({ user: { name: "John" } }, "user.name") -> "John"
 */
function getNestedValue(obj, path) {
  if (!obj || !path) {
    return obj;
  }

  const keys = path.split('.');
  let result = obj;

  for (const key of keys) {
    if (result == null) {
      return null;
    }
    result = result[key];
  }

  return result;
}

export default useGraphql;
